/**
 * Sterling app manifest.
 *
 * The shape mirrors `@mesh/brand`'s manifest so apps can interop. Each app
 * has a slug, name, tagline, and a `mark_slug` that picks one of the eight
 * Mesh-derived geometric marks (see `../components/MarkSVG.tsx`).
 *
 * Mark slugs not currently assigned to a live app sit in the `marks` array
 * with `status: "reserved"` — the suite launcher renders these as disabled
 * "Coming soon" tiles so the suite always looks fully populated.
 */
/**
 * The default manifest shipped with the package. Apps can clone-and-extend
 * via `{ ...STERLING_MANIFEST, apps: { ...STERLING_MANIFEST.apps, …new… } }`
 * and pass to `<SuiteLauncher manifest={…} />` for local testing of a new
 * app before its slug is registered.
 *
 * Mark→app pairings are mnemonic, not semantic:
 *   - Anvil ERP    → lattice     (interconnected enterprise modules)
 *   - Anvil CRM    → network     (hub-and-spoke customer network)
 *   - Help@        → convergence (paths meet — queries → support)
 *   - Shadow Lake  → waves       (continuous data substrate)
 *   - Sentinel     → topographic (terrain awareness for compliance surveillance)
 *   - Lean Playbook→ cascade     (sequential plays in layers)
 *   - Growth Playbook → bars     (growth chart)
 */
export const STERLING_MANIFEST = {
    platform: {
        name: 'Sterling',
        full_name: 'Silver Falls Sterling',
    },
    apps: {
        'anvil-erp': {
            slug: 'anvil-erp',
            name: 'Anvil ERP',
            full_name: 'Sterling Anvil ERP',
            tagline: 'Operations & finance',
            mark_slug: 'lattice',
        },
        'anvil-crm': {
            slug: 'anvil-crm',
            name: 'Anvil CRM',
            full_name: 'Sterling Anvil CRM',
            tagline: 'Accounts & pipeline',
            mark_slug: 'network',
        },
        'help-at': {
            slug: 'help-at',
            name: 'Help@',
            full_name: 'Sterling Help@',
            tagline: 'Internal helpdesk',
            mark_slug: 'convergence',
        },
        'shadow-lake': {
            slug: 'shadow-lake',
            name: 'Shadow Lake',
            full_name: 'Sterling Shadow Lake',
            tagline: 'Data lake & reporting',
            mark_slug: 'waves',
        },
        'sentinel': {
            slug: 'sentinel',
            name: 'Sentinel',
            full_name: 'Sterling Sentinel',
            tagline: 'Compliance & risk',
            mark_slug: 'topographic',
        },
        'lean-playbook': {
            slug: 'lean-playbook',
            name: 'Lean Playbook',
            full_name: 'Sterling Lean Playbook',
            tagline: 'Operational excellence',
            mark_slug: 'cascade',
        },
        'growth-playbook': {
            slug: 'growth-playbook',
            name: 'Growth Playbook',
            full_name: 'Sterling Growth Playbook',
            tagline: 'Go-to-market plays',
            mark_slug: 'bars',
        },
    },
    marks: [
        { slug: 'lattice', status: 'live' },
        { slug: 'network', status: 'live' },
        { slug: 'convergence', status: 'live' },
        { slug: 'topographic', status: 'live' },
        { slug: 'waves', status: 'live' },
        { slug: 'cascade', status: 'live' },
        { slug: 'bars', status: 'live' },
        { slug: 'bridge', status: 'reserved' },
        { slug: 'woven', status: 'reserved' },
        { slug: 'cellgrid', status: 'reserved' },
        { slug: 'portcullis', status: 'reserved' },
    ],
};
/** Convenience: get an app by slug, or throw. */
export function getApp(slug, manifest = STERLING_MANIFEST) {
    const app = manifest.apps[slug];
    if (!app) {
        throw new Error(`Sterling: no app registered with slug "${slug}". Known: ${Object.keys(manifest.apps).join(', ')}`);
    }
    return app;
}
/** Convenience: list reserved mark slugs (for unbuilt-app tiles). */
export function reservedMarkSlugs(manifest = STERLING_MANIFEST) {
    return manifest.marks.filter(m => m.status === 'reserved').map(m => m.slug);
}
//# sourceMappingURL=index.js.map